import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Save, RotateCcw, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_META = {
  mariner_boys:  { label: "Mariner Boys Hobbies",  accent: "blue",   showGroupCap: true,  weeks: 6 },
  mariner_girls: { label: "Mariner Girls Hobbies", accent: "pink",   showGroupCap: true,  weeks: 6 },
  navigator:     { label: "Navigator Hobbies",     accent: "violet", showGroupCap: false, weeks: 6 },
  intensive:     { label: "Navigator Intensives",  accent: "fuchsia", showGroupCap: false, weeks: 6 },
};

export default function ActivityCapsView({ token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [tab, setTab] = useState("intensive");
  const [relActivity, setRelActivity] = useState("");
  const [relWeek, setRelWeek] = useState("all");
  const [releasing, setReleasing] = useState(false);

  const handleRelease = async (programKey) => {
    if (!relActivity) { toast.error("Pick an activity to release"); return; }
    setReleasing(true);
    try {
      const dry = await axios.post(`${API}/admin/program-selections/clear`,
        { program: programKey, activity: relActivity, week_number: relWeek, dry_run: true }, { headers });
      const n = dry.data.count;
      if (n === 0) { toast.info(`No campers are enrolled in ${relActivity}${relWeek !== "all" ? ` for Week ${relWeek}` : ""}`); setReleasing(false); return; }
      const wkLabel = relWeek === "all" ? "ALL weeks" : `Week ${relWeek}`;
      if (!window.confirm(`Release ${n} campers from ${relActivity} (${wkLabel})?\n\nTheir spots open up and they can pick again. This cannot be undone.`)) { setReleasing(false); return; }
      const res = await axios.post(`${API}/admin/program-selections/clear`,
        { program: programKey, activity: relActivity, week_number: relWeek }, { headers });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    }
    setReleasing(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/activity-caps`, { headers });
      setData(r.data);
      setDraft({});
    } catch (e) {
      toast.error("Failed to load activity caps");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowKey = (programKey, activity) => `${programKey}::${activity}`;

  const updateDraft = (programKey, activity, patch) => {
    setDraft(d => ({
      ...d,
      [rowKey(programKey, activity)]: { ...(d[rowKey(programKey, activity)] || {}), ...patch },
    }));
  };

  const handleSaveRow = async (programKey, row) => {
    const key = rowKey(programKey, row.activity);
    const patch = draft[key] || {};
    const body = {
      program_key: programKey,
      activity: row.activity,
      per_week_cap: patch.per_week_cap ?? row.per_week_cap,
      per_group_cap: patch.per_group_cap !== undefined
        ? patch.per_group_cap
        : row.per_group_cap,
      disabled_weeks: patch.disabled_weeks ?? row.disabled_weeks ?? [],
    };
    setSavingKey(key);
    try {
      await axios.put(`${API}/admin/activity-caps`, body, { headers });
      toast.success(`Saved ${row.activity}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingKey(null);
  };

  const handleResetProgram = async (programKey) => {
    if (!window.confirm(`Reset all ${PROGRAM_META[programKey].label} caps and disabled-week toggles back to defaults?`)) return;
    try {
      const r = await axios.post(`${API}/admin/activity-caps/reset`, { program_key: programKey }, { headers });
      toast.success(`Reset ${r.data.reset} override(s) for ${PROGRAM_META[programKey].label}`);
      await load();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-caps-view">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="activity-caps-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button variant="outline" size="sm" onClick={load} data-testid="activity-caps-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-600" /> Activity Caps
          </CardTitle>
          <CardDescription>
            Per-activity per-week capacity, per-group sub-cap (Mariner only), and per-week &quot;not offered&quot; toggles.
            Changes take effect immediately. Reset to revert any program back to defaults.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-muted/50">
              {Object.entries(PROGRAM_META).map(([key, meta]) => (
                <TabsTrigger key={key} value={key} className="text-xs" data-testid={`activity-caps-tab-${key}`}>
                  {meta.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(PROGRAM_META).map(([programKey, meta]) => {
              const rows = data[programKey] || [];
              return (
                <TabsContent key={programKey} value={programKey} className="space-y-3 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {rows.length} activities · default per-week cap 20 · {meta.showGroupCap ? "default per-group cap 5" : "no per-group cap"}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => handleResetProgram(programKey)} data-testid={`activity-caps-reset-${programKey}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to defaults
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-end gap-2 p-3 rounded-xl border-2 border-rose-200 bg-rose-50/60" data-testid={`release-panel-${programKey}`}>
                    <div>
                      <p className="text-[11px] font-bold text-rose-800 mb-1">Release Enrollments — free kids from an activity (e.g. rained-out week)</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={relActivity}
                          onChange={e => setRelActivity(e.target.value)}
                          className="h-8 px-2 text-xs border rounded-md bg-white"
                          data-testid={`release-activity-select-${programKey}`}
                        >
                          <option value="">Pick activity…</option>
                          {rows.map(r => <option key={r.activity} value={r.activity}>{r.activity}</option>)}
                        </select>
                        <select
                          value={relWeek}
                          onChange={e => setRelWeek(e.target.value)}
                          className="h-8 px-2 text-xs border rounded-md bg-white"
                          data-testid={`release-week-select-${programKey}`}
                        >
                          <option value="all">All weeks</option>
                          {Array.from({ length: meta.weeks }, (_, i) => i + 1).map(wk => (
                            <option key={wk} value={wk}>Week {wk}</option>
                          ))}
                        </select>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-rose-400 text-rose-700 hover:bg-rose-100"
                          disabled={releasing || !relActivity}
                          onClick={() => handleRelease(programKey)}
                          data-testid={`release-btn-${programKey}`}
                        >
                          {releasing ? "Releasing…" : "Release Kids"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="border-b bg-stone-50">
                          <th className="text-left p-2 font-semibold">Activity</th>
                          <th className="text-center p-2 font-semibold w-24">Per-Week Cap</th>
                          {meta.showGroupCap && <th className="text-center p-2 font-semibold w-24">Per-Group Cap</th>}
                          {Array.from({ length: meta.weeks }, (_, i) => i + 1).map(wk => (
                            <th key={wk} className="text-center p-2 font-semibold w-12">Wk {wk}</th>
                          ))}
                          <th className="text-center p-2 font-semibold w-20">Save</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(row => {
                          const key = rowKey(programKey, row.activity);
                          const d = draft[key] || {};
                          const perWeek = d.per_week_cap ?? row.per_week_cap;
                          const perGroup = d.per_group_cap !== undefined ? d.per_group_cap : row.per_group_cap;
                          const disabled = d.disabled_weeks ?? row.disabled_weeks ?? [];
                          const dirty = Object.keys(d).length > 0;
                          return (
                            <tr key={row.activity} className="border-b hover:bg-stone-50" data-testid={`activity-caps-row-${programKey}-${row.activity.replace(/\s+/g, '-')}`}>
                              <td className="p-2 font-medium">{row.activity}</td>
                              <td className="p-2 text-center">
                                <Input
                                  type="number"
                                  min="1"
                                  max="500"
                                  value={perWeek}
                                  onChange={e => updateDraft(programKey, row.activity, { per_week_cap: parseInt(e.target.value, 10) || 0 })}
                                  className="w-20 h-8 text-center text-xs"
                                  data-testid={`activity-caps-perweek-${programKey}-${row.activity.replace(/\s+/g, '-')}`}
                                />
                              </td>
                              {meta.showGroupCap && (
                                <td className="p-2 text-center">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={perGroup ?? ""}
                                    placeholder="—"
                                    onChange={e => {
                                      const v = e.target.value.trim();
                                      updateDraft(programKey, row.activity, { per_group_cap: v === "" ? null : (parseInt(v, 10) || 0) });
                                    }}
                                    className="w-20 h-8 text-center text-xs"
                                    data-testid={`activity-caps-pergroup-${programKey}-${row.activity.replace(/\s+/g, '-')}`}
                                  />
                                </td>
                              )}
                              {Array.from({ length: meta.weeks }, (_, i) => i + 1).map(wk => {
                                const isOff = disabled.includes(wk);
                                return (
                                  <td key={wk} className="p-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={isOff}
                                      onChange={e => {
                                        const next = e.target.checked
                                          ? [...new Set([...disabled, wk])].sort((a, b) => a - b)
                                          : disabled.filter(w => w !== wk);
                                        updateDraft(programKey, row.activity, { disabled_weeks: next });
                                      }}
                                      className="w-4 h-4 accent-rose-600 cursor-pointer"
                                      title={isOff ? `Not offered Week ${wk}` : `Offered Week ${wk}`}
                                      data-testid={`activity-caps-wk-${programKey}-${row.activity.replace(/\s+/g, '-')}-${wk}`}
                                    />
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center">
                                <Button
                                  size="sm"
                                  variant={dirty ? "default" : "outline"}
                                  disabled={!dirty || savingKey === key}
                                  onClick={() => handleSaveRow(programKey, row)}
                                  className="h-7 text-xs"
                                  data-testid={`activity-caps-save-${programKey}-${row.activity.replace(/\s+/g, '-')}`}
                                >
                                  <Save className="w-3 h-3 mr-1" />
                                  {savingKey === key ? "…" : "Save"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-[10px] text-muted-foreground">
                    Checkbox under a week = activity is <strong>not offered</strong> that week (campers see it greyed out with a &quot;Not offered Week N&quot; badge and server rejects attempts to pick it). Leave per-group cap empty for no sub-cap.
                  </p>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
